import React, { useState, useRef, useEffect, useMemo } from 'react'
import { File, FileText, FileCsv, FilePdf, FileImage, X, Upload, CheckCircle } from 'phosphor-react'
import { FileData } from '@/types'
import { getLogoUrlFromFileData } from '@/utils/branding'

interface FileInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange'
> {
  label?: string
  error?: string
  helperText?: string
  accept?: string
  multiple?: boolean
  onChange?: (files: FileList | null) => void
  onFileRemove?: (index: number) => void
  selectedFiles?: File[]
  /** Persisted file shown when no new file is selected (e.g. saved logo). */
  storedPreview?: FileData
  previewAlt?: string
}

const isImageFile = (file: File): boolean => file.type.startsWith('image/')

const getFileIcon = (file: File): React.ReactNode => {
  const extension = file.name.split('.').pop()?.toLowerCase()
  const size = 'w-8 h-8'
  const className = `text-[var(--color-primary)] ${size}`

  switch (extension) {
    case 'csv':
      return <FileCsv className={className} weight="regular" />
    case 'pdf':
      return <FilePdf className={className} weight="regular" />
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'svg':
    case 'webp':
      return <FileImage className={className} weight="regular" />
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return <File className={className} weight="regular" />
    case 'txt':
    case 'md':
      return <FileText className={className} weight="regular" />
    case 'p12':
    case 'pfx':
    case 'cer':
    case 'crt':
    case 'pem':
      return <File className={className} weight="regular" />
    default:
      return <File className={className} weight="regular" />
  }
}

const getFileTypeLabel = (file: File): string => {
  const extension = file.name.split('.').pop()?.toUpperCase()
  return extension || 'Fichier'
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const dropZoneClassName = ({
  isDragging,
  hasError,
  isDisabled,
}: {
  isDragging: boolean
  hasError: boolean
  isDisabled: boolean
}) =>
  [
    'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-all duration-200',
    isDragging
      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
      : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-gray-50)]',
    hasError ? 'border-[var(--color-error)]' : '',
    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
  ].join(' ')

const ImagePreviewPanel: React.FC<{ src: string; alt: string; compact?: boolean }> = ({
  src,
  alt,
  compact = false,
}) => (
  <div
    className={`flex items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-gray-50)] ${
      compact ? 'p-2' : 'min-h-[140px] p-4'
    }`}
  >
    <img
      src={src}
      alt={alt}
      className={`object-contain rounded-md ${compact ? 'h-16 w-16' : 'max-h-24 max-w-full'}`}
    />
  </div>
)

export const FileInput: React.FC<FileInputProps> = ({
  label,
  error,
  helperText,
  accept,
  multiple = false,
  onChange,
  onFileRemove,
  selectedFiles,
  storedPreview,
  previewAlt = 'Aperçu du fichier',
  disabled,
  ...props
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>(selectedFiles || [])
  const [isDragging, setIsDragging] = useState(false)

  const storedPreviewUrl = useMemo(
    () => getLogoUrlFromFileData(storedPreview),
    [storedPreview]
  )

  const imagePreviewUrls = useMemo(() => {
    return files.map(file => (isImageFile(file) ? URL.createObjectURL(file) : null))
  }, [files])

  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach(url => {
        if (url) URL.revokeObjectURL(url)
      })
    }
  }, [imagePreviewUrls])

  // Sync with selectedFiles prop when it changes
  useEffect(() => {
    if (selectedFiles !== undefined) {
      setFiles(selectedFiles)
    }
  }, [selectedFiles])

  const handleFileChange = (newFiles: FileList | null) => {
    if (!newFiles || newFiles.length === 0) return

    const fileArray = Array.from(newFiles)
    if (multiple) {
      setFiles(prev => [...prev, ...fileArray])
      onChange?.(newFiles)
    } else {
      setFiles(fileArray)
      onChange?.(newFiles)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e.target.files)
  }

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index)
    setFiles(newFiles)
    onFileRemove?.(index)
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileChange(e.dataTransfer.files)
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const reactId = React.useId()
  const inputId = props.id || `file-input-${reactId}`
  const showStoredPreview = files.length === 0 && Boolean(storedPreviewUrl)
  const showDropZone = files.length === 0

  const dropZoneHandlers = {
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onClick: disabled ? undefined : handleClick,
  }

  const dropZoneContent = (
    <>
      <div className="mb-3 rounded-full bg-[var(--color-primary)]/10 p-3">
        <Upload className="h-6 w-6 text-[var(--color-primary)]" weight="regular" />
      </div>
      <p className="mb-1 text-sm font-medium text-[var(--color-text-primary)]">
        Cliquez pour télécharger ou glissez-déposez
      </p>
      <p className="text-xs text-[var(--color-text-muted)]">
        {accept ? `Types acceptés : ${accept}` : 'Tous les types de fichiers'}
      </p>
    </>
  )

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5"
        >
          {label}
          {props.required && <span className="text-[var(--color-error)] ml-1">*</span>}
        </label>
      )}

      {/* File Input (hidden) */}
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        disabled={disabled}
        className="hidden"
        {...props}
      />

      {files.length > 0 ? (
        <div className="space-y-2">
          {files.map((file, index) => {
            const imageUrl = imagePreviewUrls[index]

            return (
              <div
                key={`${file.name}-${index}`}
                className={`rounded-lg border border-[var(--color-border)] bg-[var(--color-gray-50)] p-3 transition-colors hover:bg-[var(--color-gray-100)] ${
                  imageUrl && !multiple
                    ? 'grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr_auto] sm:items-center'
                    : 'flex items-center gap-3'
                }`}
              >
                <div className="flex shrink-0 justify-center sm:justify-start">
                  {imageUrl ? (
                    <ImagePreviewPanel src={imageUrl} alt={file.name} compact={multiple} />
                  ) : (
                    getFileIcon(file)
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                      {file.name}
                    </p>
                    <span className="shrink-0 rounded bg-[var(--color-gray-200)] px-2 py-0.5 text-xs font-medium text-[var(--color-text-muted)]">
                      {getFileTypeLabel(file)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                    {formatFileSize(file.size)}
                  </p>
                </div>

                <div className="flex items-center justify-end gap-2 sm:justify-center">
                  <CheckCircle className="h-5 w-5 text-[var(--color-success)]" weight="fill" />
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    disabled={disabled}
                    className="rounded p-1 text-[var(--color-error)] transition-colors hover:bg-[var(--color-error)]/10 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Supprimer"
                  >
                    <X className="h-4 w-4" weight="regular" />
                  </button>
                </div>
              </div>
            )
          })}

          {multiple && (
            <button
              type="button"
              onClick={handleClick}
              disabled={disabled}
              className="mt-2 w-full rounded-lg border border-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary)] transition-colors hover:bg-[var(--color-primary)]/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Ajouter d&apos;autres fichiers
            </button>
          )}
        </div>
      ) : (
        <div
          className={`grid gap-4 ${
            showStoredPreview ? 'grid-cols-1 sm:grid-cols-[minmax(0,200px)_1fr]' : 'grid-cols-1'
          }`}
        >
          {showStoredPreview && storedPreviewUrl && (
            <ImagePreviewPanel src={storedPreviewUrl} alt={previewAlt} />
          )}

          {showDropZone && (
            <div
              {...dropZoneHandlers}
              className={dropZoneClassName({
                isDragging,
                hasError: Boolean(error),
                isDisabled: Boolean(disabled),
              })}
            >
              {dropZoneContent}
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="mt-1.5 text-sm text-[var(--color-error)]" role="alert">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-1.5 text-sm text-[var(--color-text-muted)]">{helperText}</p>
      )}
    </div>
  )
}
